import { type RegionBox, useImageRegions, useUploadImageForRegions } from "@kupikupi/api";
import { CloseIcon } from "@kupikupi/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cropImageToBase64, type PixelRect } from "../../lib/crop-image";
import { dataUrlToBase64, downscaleForImageSearch } from "../../lib/downscale-image";
import type { SearchSessionContext } from "../../lib/image-search-session";
import {
  clampPercentRect,
  hitTestRegionPercent,
  type NaturalDims,
  naturalRegionToPercentRect,
  type PercentRect,
  percentRectMatches,
  pickLargestRegion,
} from "../../lib/region-geometry";

interface Props {
  /** Displayable image src (a stable data-URL produced by prepareImageSearchSrc). */
  src: string;
  /** User tapped "Find": returns cropped pure base64 (no data-URL prefix). */
  onConfirm: (croppedBase64: string, context: SearchSessionContext) => void;
  onCancel: () => void;
  initialContext?: SearchSessionContext | null;
}

// Default selection = whole image (percent). Fallback when no region detected.
const FULL_CROP: PercentRect = { x: 0, y: 0, width: 100, height: 100 };
// Min valid selection size (% of the image edge); smaller boxes aren't seeded/selectable.
const MIN_PCT = 2;
// Region detect always runs on 1688 (only it has YOLO); cropped image still
// carries channel=1688 into catalog (matches web spec §5.1C).
const DETECT_CHANNEL = "1688" as const;
// Touch-friendly corner-handle hit radius (px). Visual dot is smaller (CSS).
const HANDLE_HIT = 28;

type DragMode =
  | { kind: "move"; startX: number; startY: number; origin: PercentRect }
  | { kind: "corner"; corner: "nw" | "ne" | "se" | "sw"; origin: PercentRect }
  | null;

/**
 * Mini-App region-select crop overlay — a touch-native port of
 * apps/web/components/image-search/crop-modal.tsx.
 *
 * Telegram / small-screen differences vs Web:
 *  - No react-image-crop dependency (keeps the Mini bundle small + gives us full
 *    control over touch gestures on a 390px viewport). The selection box and its
 *    four corner handles are dragged via Pointer Events (works for touch + mouse),
 *    and dragging *inside* the box moves it. Corner hit areas are enlarged
 *    (HANDLE_HIT) so they're tappable with a finger.
 *  - Same coordinate model as web: everything is percentages (0–100) relative to
 *    the downscaled image, so it's render-size independent and aligns the
 *    detect boxes / overlay dots / final crop.
 *  - WB-style overlay: each detected candidate gets a tappable centre dot; the
 *    selected one is wrapped by the highlight frame (no dot). Tap a dot or inside
 *    a box to move the highlight there.
 *  - Loading: a blocking spinner while downscaling (no image yet), and a
 *    non-blocking spinner overlay while region detection runs (image is shown,
 *    user can already crop/search the whole image).
 */
export function CropModal({ src, onConfirm, onCancel, initialContext = null }: Props) {
  const { t } = useTranslation("common");
  const [crop, setCrop] = useState<PercentRect>(FULL_CROP);

  // ── Downscale before detect (perf + coordinate source-of-truth) ──
  const [displaySrc, setDisplaySrc] = useState("");
  const [scaledDims, setScaledDims] = useState<NaturalDims | null>(null);
  const [downscaling, setDownscaling] = useState(true);
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setDisplaySrc("");
    setScaledDims(null);
    setDownscaling(true);
    setSeeded(false);
    setCrop(FULL_CROP);
    (async () => {
      const { dataUrl, width, height } = await downscaleForImageSearch(src);
      if (!cancelled) {
        setDisplaySrc(dataUrl);
        setScaledDims(
          width > 0 && height > 0 ? { naturalWidth: width, naturalHeight: height } : null,
        );
        setDownscaling(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src]);

  const cachedDetect =
    initialContext?.detectContext?.channel === DETECT_CHANNEL ? initialContext.detectContext : null;
  const cachedRegionList = cachedDetect?.regionList ?? [];
  const hasCachedDetectContext = !!cachedDetect;

  // ── Region detect (progressive enhancement, non-blocking) ──
  const regionUploadMut = useUploadImageForRegions();
  const [detectImageId, setDetectImageId] = useState("");
  useEffect(() => {
    if (hasCachedDetectContext) {
      setDetectImageId(cachedDetect?.detectImageId ?? "");
      return;
    }
    if (!displaySrc) return;
    let cancelled = false;
    setDetectImageId("");
    (async () => {
      try {
        const base64 = dataUrlToBase64(displaySrc);
        if (!base64 || cancelled) return;
        const id = await regionUploadMut.mutateAsync({
          imageBase64: base64,
          channel: DETECT_CHANNEL,
        });
        if (!cancelled) setDetectImageId(id);
      } catch {
        // Detect stash failed = degrade to manual crop, silent.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displaySrc, hasCachedDetectContext, cachedDetect?.detectImageId]);

  const regionsQ = useImageRegions({
    imageId: hasCachedDetectContext ? "" : detectImageId,
    channel: DETECT_CHANNEL,
  });
  const regionList = hasCachedDetectContext ? cachedRegionList : (regionsQ.data?.regionList ?? []);
  const currentRegion = hasCachedDetectContext
    ? (cachedDetect?.currentRegion ?? null)
    : (regionsQ.data?.currentRegion ?? null);
  const hasRegions = regionList.length > 0;
  const detectingRegions =
    !hasCachedDetectContext &&
    !downscaling &&
    (regionUploadMut.isPending || regionsQ.isLoading || regionsQ.isFetching);

  // Default selected box = largest region; fallback to backend currentRegion.
  const defaultRegion = pickLargestRegion(regionList) ?? currentRegion;
  const defaultPct =
    scaledDims && defaultRegion ? naturalRegionToPercentRect(defaultRegion, scaledDims) : null;

  // Deterministic seed: once region + dims are known, set the selection to the
  // largest box (percent). Only seeds once; reset on src change above.
  useEffect(() => {
    if (seeded || !defaultPct) return;
    if (defaultPct.width < MIN_PCT || defaultPct.height < MIN_PCT) return;
    setCrop(clampPercentRect(defaultPct));
    setSeeded(true);
  }, [seeded, defaultPct]);

  const selectRect = useCallback((r: PercentRect) => {
    if (r.width < MIN_PCT || r.height < MIN_PCT) return;
    setCrop(clampPercentRect(r));
  }, []);

  // ── Touch / pointer gesture handling ──
  const imgWrapRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragMode>(null);

  const wrapRect = () => imgWrapRef.current?.getBoundingClientRect() ?? null;

  // Convert a client point to percent coords relative to the image wrap.
  const toPercent = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const rect = wrapRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return null;
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100,
    };
  };

  const onPointerDownCorner = (corner: "nw" | "ne" | "se" | "sw") => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { kind: "corner", corner, origin: crop };
  };

  const onPointerDownBox = (e: React.PointerEvent) => {
    // Start a move drag when pressing inside the selection (not on a handle).
    const p = toPercent(e.clientX, e.clientY);
    if (!p) return;
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { kind: "move", startX: p.x, startY: p.y, origin: crop };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const p = toPercent(e.clientX, e.clientY);
    if (!p) return;
    e.preventDefault();
    if (drag.kind === "move") {
      const dx = p.x - drag.startX;
      const dy = p.y - drag.startY;
      const nextX = drag.origin.x + dx;
      const nextY = drag.origin.y + dy;
      setCrop({
        x: Math.min(Math.max(nextX, 0), 100 - drag.origin.width),
        y: Math.min(Math.max(nextY, 0), 100 - drag.origin.height),
        width: drag.origin.width,
        height: drag.origin.height,
      });
      return;
    }
    // corner resize: keep the opposite corner fixed.
    const o = drag.origin;
    const left = o.x;
    const top = o.y;
    const right = o.x + o.width;
    const bottom = o.y + o.height;
    const px = Math.min(Math.max(p.x, 0), 100);
    const py = Math.min(Math.max(p.y, 0), 100);
    let nx = left;
    let ny = top;
    let nw = o.width;
    let nh = o.height;
    if (drag.corner === "nw") {
      nx = Math.min(px, right - MIN_PCT);
      ny = Math.min(py, bottom - MIN_PCT);
      nw = right - nx;
      nh = bottom - ny;
    } else if (drag.corner === "ne") {
      ny = Math.min(py, bottom - MIN_PCT);
      nw = Math.max(px - left, MIN_PCT);
      nh = bottom - ny;
    } else if (drag.corner === "se") {
      nw = Math.max(px - left, MIN_PCT);
      nh = Math.max(py - top, MIN_PCT);
    } else {
      // sw
      nx = Math.min(px, right - MIN_PCT);
      nw = right - nx;
      nh = Math.max(py - top, MIN_PCT);
    }
    setCrop(clampPercentRect({ x: nx, y: ny, width: nw, height: nh }));
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (dragRef.current) {
      (e.target as Element).releasePointerCapture?.(e.pointerId);
    }
    dragRef.current = null;
  };

  // Tap the image (not on the box) → if it hits a detected region, select it.
  const onImageTap = (e: React.PointerEvent) => {
    if (dragRef.current) return;
    if (!hasRegions || !scaledDims) return;
    const p = toPercent(e.clientX, e.clientY);
    if (!p) return;
    const idx = hitTestRegionPercent(p.x, p.y, regionList, scaledDims);
    if (idx < 0) return;
    selectRect(naturalRegionToPercentRect(regionList[idx] as RegionBox, scaledDims));
  };

  // ── confirm: cut out the current percent crop → base64 ──
  const handleConfirm = () => {
    if (downscaling) return;
    const image = imgWrapRef.current?.querySelector("img") as HTMLImageElement | null;
    if (!image || image.naturalWidth === 0 || image.width === 0) return;
    const pxRect: PixelRect = {
      x: (Math.max(0, crop.x) / 100) * image.width,
      y: (Math.max(0, crop.y) / 100) * image.height,
      width: (crop.width / 100) * image.width,
      height: (crop.height / 100) * image.height,
    };
    if (pxRect.width <= 0 || pxRect.height <= 0) return;
    try {
      onConfirm(cropImageToBase64(image, pxRect), buildConfirmContext());
    } catch {
      // crop failed (no 2d context) → no-op; caller can still close.
    }
  };

  const handleFullImageSearch = () => {
    if (downscaling || !displaySrc) return;
    const base64 = dataUrlToBase64(displaySrc);
    if (!base64) return;
    onConfirm(base64, buildConfirmContext());
  };

  const buildConfirmContext = (): SearchSessionContext => {
    const context: SearchSessionContext = {};
    if (displaySrc) {
      context.sourceImage = { dataUrl: displaySrc };
      if (scaledDims?.naturalWidth) context.sourceImage.width = scaledDims.naturalWidth;
      if (scaledDims?.naturalHeight) context.sourceImage.height = scaledDims.naturalHeight;
    } else if (initialContext?.sourceImage) {
      context.sourceImage = initialContext.sourceImage;
    }
    if (hasRegions || currentRegion || detectImageId || cachedDetect?.detectImageId) {
      context.detectContext = { channel: DETECT_CHANNEL, regionList, currentRegion };
      const id = detectImageId || cachedDetect?.detectImageId;
      if (id) context.detectContext.detectImageId = id;
    } else if (initialContext?.detectContext) {
      context.detectContext = initialContext.detectContext;
    }
    return context;
  };

  const hintText =
    downscaling || detectingRegions
      ? null
      : hasRegions
        ? t("image_search.crop_detected_hint")
        : t("image_search.crop_region_fallback");

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss; Escape handled by parent
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="kk-mini-crop-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(11, 12, 15, 0.6)",
        zIndex: 1100,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: "var(--color-surface, #fff)",
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          padding: "16px 14px calc(14px + env(safe-area-inset-bottom, 0px))",
          width: "100%",
          maxHeight: "92vh",
          overflowY: "auto",
          boxShadow: "0 -8px 30px rgba(0,0,0,0.22)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <h2 id="kk-mini-crop-title" style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>
            {t("image_search.crop_title")}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label={t("image_search.close")}
            style={{
              background: "transparent",
              border: 0,
              cursor: "pointer",
              color: "var(--color-text-muted)",
              padding: 4,
              display: "inline-flex",
            }}
          >
            <CloseIcon size={20} />
          </button>
        </div>

        {hintText && (
          <p
            role="status"
            aria-live="polite"
            style={{
              margin: "0 0 10px",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--color-text-muted)",
              textAlign: "center",
            }}
          >
            {hintText}
          </p>
        )}

        {downscaling ? (
          <div
            data-testid="kk-mini-detecting-spinner"
            role="status"
            aria-live="polite"
            aria-label={t("image_search.crop_detecting")}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              minHeight: 180,
              padding: "24px 16px",
            }}
          >
            <div className="kk-mini-spinner" aria-hidden />
            <p
              style={{
                margin: 0,
                fontSize: 13,
                fontWeight: 600,
                color: "var(--color-text-muted)",
                textAlign: "center",
              }}
            >
              {t("image_search.crop_detecting")}
            </p>
          </div>
        ) : (
          <div style={{ display: "grid", placeItems: "center" }}>
            {/* Image wrap: selection + dots are absolutely positioned over <img>. */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: pointer crop surface */}
            <div
              ref={imgWrapRef}
              style={{
                position: "relative",
                display: "inline-block",
                maxWidth: "100%",
                touchAction: "none",
                userSelect: "none",
                WebkitUserSelect: "none",
              }}
              onPointerUp={onImageTap}
            >
              {displaySrc && (
                // biome-ignore lint/performance/noImgElement: data-URL image, not a CDN asset
                <img
                  src={displaySrc}
                  alt={t("image_search.crop_title")}
                  draggable={false}
                  style={{
                    maxHeight: "56vh",
                    maxWidth: "100%",
                    display: "block",
                    pointerEvents: "none",
                  }}
                />
              )}

              {/* Darken outside the selection (4 panels, clipped to the wrap). */}
              {!detectingRegions && <MaskPanels crop={crop} />}

              {/* Selection box + corner handles (drag to move / resize). */}
              {!detectingRegions && (
                <div
                  data-testid="kk-mini-crop-box"
                  onPointerDown={onPointerDownBox}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                  style={{
                    position: "absolute",
                    left: `${crop.x}%`,
                    top: `${crop.y}%`,
                    width: `${crop.width}%`,
                    height: `${crop.height}%`,
                    border: "2.5px solid var(--color-primary, #0BA888)",
                    borderRadius: 10,
                    boxShadow: "0 0 0 1.5px rgba(255,255,255,0.9), 0 0 0 4px rgba(11,168,136,0.25)",
                    cursor: "move",
                    touchAction: "none",
                  }}
                >
                  {(["nw", "ne", "se", "sw"] as const).map((c) => (
                    <span
                      key={c}
                      data-testid={`kk-mini-handle-${c}`}
                      onPointerDown={onPointerDownCorner(c)}
                      onPointerMove={onPointerMove}
                      onPointerUp={onPointerUp}
                      onPointerCancel={onPointerUp}
                      style={{
                        position: "absolute",
                        ...cornerPos(c),
                        width: HANDLE_HIT,
                        height: HANDLE_HIT,
                        transform: "translate(-50%, -50%)",
                        display: "grid",
                        placeItems: "center",
                        touchAction: "none",
                        cursor: cornerCursor(c),
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          background: "#fff",
                          border: "2px solid var(--color-primary, #0BA888)",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                        }}
                      />
                    </span>
                  ))}
                </div>
              )}

              {/* Candidate dots (WB pattern): one per non-selected region. */}
              {!detectingRegions && hasRegions && scaledDims && (
                <RegionOverlay
                  regions={regionList}
                  dims={scaledDims}
                  crop={crop}
                  onSelect={(region) => selectRect(naturalRegionToPercentRect(region, scaledDims))}
                  dotLabel={t("image_search.crop_detected_hint")}
                />
              )}

              {/* Non-blocking detect spinner overlay (image already shown). */}
              {detectingRegions && (
                <div
                  data-testid="kk-mini-detecting-spinner"
                  role="status"
                  aria-live="polite"
                  aria-label={t("image_search.crop_detecting")}
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    background: "rgba(255,255,255,0.55)",
                    pointerEvents: "none",
                  }}
                >
                  <div className="kk-mini-spinner" aria-hidden />
                  <p
                    style={{
                      margin: 0,
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--color-text-muted)",
                      textAlign: "center",
                    }}
                  >
                    {t("image_search.crop_detecting")}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div
          data-testid="kk-mini-crop-actions"
          style={{ display: "flex", gap: 10, alignItems: "stretch", marginTop: 14 }}
        >
          <button
            type="button"
            onClick={handleFullImageSearch}
            disabled={downscaling}
            style={{
              flex: "0 0 clamp(110px, 34%, 150px)",
              minWidth: 0,
              padding: "11px 10px",
              background: "#fff",
              color: "var(--color-primary-fill, #123329)",
              border: "1px solid var(--color-primary, #0BA888)",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1.2,
              cursor: downscaling ? "not-allowed" : "pointer",
              opacity: downscaling ? 0.6 : 1,
              whiteSpace: "normal",
            }}
          >
            {t("image_search.crop_full_image")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={downscaling}
            aria-busy={downscaling || detectingRegions}
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              padding: "12px 16px",
              background: downscaling
                ? "var(--color-text-muted, #9ca3af)"
                : "var(--color-cta, #FF6B4A)",
              color: "#fff",
              border: 0,
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 800,
              cursor: downscaling ? "not-allowed" : "pointer",
              opacity: downscaling ? 0.6 : 1,
            }}
          >
            {t("image_search.crop_confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Four dark panels around the selection (clipped to the image wrap; no shadow
 *  spread that could bleed past the modal — the web 9999px-spread bug). */
function MaskPanels({ crop }: { crop: PercentRect }) {
  const dark = "rgba(0,0,0,0.32)";
  const panel = (s: React.CSSProperties): React.CSSProperties => ({
    position: "absolute",
    background: dark,
    pointerEvents: "none",
    ...s,
  });
  return (
    <>
      {/* top */}
      <div style={panel({ left: 0, top: 0, width: "100%", height: `${crop.y}%` })} />
      {/* bottom */}
      <div
        style={panel({
          left: 0,
          top: `${crop.y + crop.height}%`,
          width: "100%",
          bottom: 0,
        })}
      />
      {/* left */}
      <div
        style={panel({
          left: 0,
          top: `${crop.y}%`,
          width: `${crop.x}%`,
          height: `${crop.height}%`,
        })}
      />
      {/* right */}
      <div
        style={panel({
          left: `${crop.x + crop.width}%`,
          top: `${crop.y}%`,
          right: 0,
          height: `${crop.height}%`,
        })}
      />
    </>
  );
}

function RegionOverlay({
  regions,
  dims,
  crop,
  onSelect,
  dotLabel,
}: {
  regions: RegionBox[];
  dims: NaturalDims;
  crop: PercentRect;
  onSelect: (region: RegionBox) => void;
  dotLabel: string;
}) {
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {regions.map((region, i) => {
        const r = naturalRegionToPercentRect(region, dims);
        if (r.width <= 0 || r.height <= 0) return null;
        if (percentRectMatches(crop, r)) return null;
        const cx = r.x + r.width / 2;
        const cy = r.y + r.height / 2;
        return (
          <button
            // biome-ignore lint/suspicious/noArrayIndexKey: box order is stable (single backend response)
            key={i}
            type="button"
            data-testid="kk-mini-region-dot"
            className="kk-mini-region-dot"
            aria-label={dotLabel}
            onClick={() => onSelect(region)}
            style={{ left: `${cx}%`, top: `${cy}%` }}
          />
        );
      })}
    </div>
  );
}

function cornerPos(c: "nw" | "ne" | "se" | "sw"): React.CSSProperties {
  switch (c) {
    case "nw":
      return { left: 0, top: 0 };
    case "ne":
      return { left: "100%", top: 0 };
    case "se":
      return { left: "100%", top: "100%" };
    default:
      return { left: 0, top: "100%" };
  }
}

function cornerCursor(c: "nw" | "ne" | "se" | "sw"): string {
  return c === "nw" || c === "se" ? "nwse-resize" : "nesw-resize";
}
